import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class QrCodeService {
  constructor() {}

  /**
   * Generate QR code as data URL using the angularx-qrcode library
   * This service provides a way to generate QR codes programmatically
   * for printing and other non-display purposes
   */
  async generateQRCodeAsDataURL(
    data: string,
    options: {
      width?: number;
      height?: number;
      errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
      margin?: number;
    } = {}
  ): Promise<string> {
    const {
      width = 200,
      height = 200,
      errorCorrectionLevel = 'M',
      margin = 1,
    } = options;

    try {
      // Create a temporary div to render the QR code component
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = `${width}px`;
      tempDiv.style.height = `${height}px`;

      // Create the QR code element using the angularx-qrcode library
      const qrElement = document.createElement('qrcode');
      qrElement.setAttribute('qrdata', data);
      qrElement.setAttribute('width', width.toString());
      qrElement.setAttribute('height', height.toString());
      qrElement.setAttribute('errorCorrectionLevel', errorCorrectionLevel);
      qrElement.setAttribute('elementType', 'img');
      qrElement.setAttribute('cssClass', 'temp-qr-code');

      tempDiv.appendChild(qrElement);
      document.body.appendChild(tempDiv);

      // Wait for the QR code to render
      return new Promise((resolve, reject) => {
        const checkForImage = () => {
          const img = tempDiv.querySelector('img');
          if (img && img.src) {
            const dataURL = img.src;
            // Clean up
            document.body.removeChild(tempDiv);
            resolve(dataURL);
          } else {
            // Check again after a short delay
            setTimeout(checkForImage, 100);
          }
        };

        // Start checking for the image
        checkForImage();

        // Timeout after 5 seconds
        setTimeout(() => {
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
            reject(new Error('QR code generation timeout'));
          }
        }, 5000);
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      // Return a fallback data URL
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    }
  }

  /**
   * Generate QR code as canvas element
   */
  async generateQRCodeAsCanvas(
    data: string,
    options: {
      width?: number;
      height?: number;
      errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
      margin?: number;
    } = {}
  ): Promise<HTMLCanvasElement> {
    const {
      width = 200,
      height = 200,
      errorCorrectionLevel = 'M',
      margin = 1,
    } = options;

    try {
      // Generate as data URL first, then convert to canvas
      const dataURL = await this.generateQRCodeAsDataURL(data, options);

      // Create canvas and draw the image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // Create image and draw to canvas
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas);
        };
        img.onerror = () => reject(new Error('Failed to load QR code image'));
        img.src = dataURL;
      });
    } catch (error) {
      console.error('Error generating QR code canvas:', error);
      throw new Error('Failed to generate QR code canvas');
    }
  }

  /**
   * Generate QR code as SVG string
   */
  async generateQRCodeAsSVG(
    data: string,
    options: {
      width?: number;
      height?: number;
      errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
      margin?: number;
    } = {}
  ): Promise<string> {
    const {
      width = 200,
      height = 200,
      errorCorrectionLevel = 'M',
      margin = 1,
    } = options;

    try {
      // For now, return a simple SVG placeholder
      // In a production environment, you would use a proper QR code library
      const svg = `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
          <rect width="100%" height="100%" fill="white"/>
          <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12" fill="black">
            QR Code Placeholder
          </text>
        </svg>
      `;

      return svg;
    } catch (error) {
      console.error('Error generating QR code SVG:', error);
      throw new Error('Failed to generate QR code SVG');
    }
  }
}
